import React, { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ArrowRight, Check, Info, Loader2, Search, X } from "lucide-react";
import { Badge } from "@/components/Badge/Badge";
import KpiRelationsLine from "../KpiRelationsLine/KpiRelationsLine";
import { Button } from "@/components/Button/Button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AccountMappingRow } from "../../api/fetchAccountMappings/types";
import { LedgerAccount } from "../../api/fetchLedgerAccounts/types";
import { LedgerAccountRow } from "../../hooks/useAccountMappingSection";
import {
  PickerCandidate,
  PickerTier,
  PICKER_TIER_LABELS,
} from "../../hooks/useAccountPickerDrawer";
import { LEDGER_ACCOUNT_DESCRIPTIONS } from "../../config/accountMappingDisplay";
import { InactiveBadge } from "../InactiveBadge/InactiveBadge";

interface AccountPickerDrawerProps {
  accountsInTarget: AccountMappingRow[];
  candidates: PickerCandidate[];
  hiddenInactiveCount: number;
  rows: LedgerAccountRow[];
  movingCount: number;
  nextNeedy: LedgerAccount | null;
  onClose: () => void;
  onCommit: () => void;
  onConfirm: (qboAccountId: string) => void;
  onSelectAllInTier: (tier: PickerTier) => void;
  onSwitchTarget: (ledgerAccountCode: string) => void;
  onToggleInactive: (showInactive: boolean) => void;
  onToggleSelected: (qboAccountId: string) => void;
  onUnassign: (qboAccountId: string) => void;
  query: string;
  savingAccountIds: Set<string>;
  selectedCount: number;
  selectedIds: Set<string>;
  setQuery: (query: string) => void;
  showInactive: boolean;
  target: LedgerAccount | null;
}

/**
 * The drawer's top-level divider. Deliberately heavier than the tier headings
 * inside the pick list — uppercase and bolder on a darker ground — so the two
 * levels do not read as the same band repeated down the panel.
 */
const SectionHeading: React.FC<{
  action?: React.ReactNode;
  count?: number;
  title: string;
}> = ({ action, count, title }) => (
  <div className="flex items-center justify-between gap-2 border-y border-gray-200 bg-gray-100 px-5 py-2 dark:border-slate-700 dark:bg-slate-800">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
      {title}
    </span>
    <span className="flex items-center gap-3">
      {action}
      {count !== undefined && (
        <span className="text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
          {count}
        </span>
      )}
    </span>
  </div>
);

export const AccountPickerDrawer: React.FC<AccountPickerDrawerProps> = ({
  accountsInTarget,
  candidates,
  hiddenInactiveCount,
  movingCount,
  nextNeedy,
  onClose,
  onCommit,
  onConfirm,
  onSelectAllInTier,
  onSwitchTarget,
  onToggleInactive,
  onToggleSelected,
  onUnassign,
  query,
  rows,
  savingAccountIds,
  selectedCount,
  selectedIds,
  setQuery,
  showInactive,
  target,
}) => {
  // Typed as the element the dialog's `initialFocus` wants, so opening the
  // drawer lands on the search box rather than the close button.
  const searchRef = useRef<HTMLElement | null>(null);

  /**
   * `target` clears the moment the drawer starts closing, but the panel is
   * still on screen sliding out, so the last one is kept to render against.
   */
  const [lastTarget, setLastTarget] = useState<LedgerAccount | null>(null);
  const openTarget = target ?? lastTarget;

  useEffect(() => {
    if (target) {
      setLastTarget(target);
    }
  }, [target]);
  const targetCode = target?.code;

  // Refocus search when the member switches account without closing.
  useEffect(() => {
    if (targetCode) {
      searchRef.current?.focus();
    }
  }, [targetCode]);

  const rowsByTypeLabel = rows.reduce<Record<string, LedgerAccountRow[]>>(
    (grouped, row) => {
      const typeLabel = row.ledgerAccount.ledgerAccountTypeLabel;
      return { ...grouped, [typeLabel]: [...(grouped[typeLabel] ?? []), row] };
    },
    {},
  );

  /**
   * Alphabetical here, unlike the page behind, which lists sections in
   * statement order. This is a lookup — the member arrives knowing the name of
   * the account they want and scans for the word — so the order that helps is
   * the one a dictionary uses. Accounts within a section already arrive sorted
   * by label from the API.
   */
  const switcherGroups = Object.entries(rowsByTypeLabel).sort(
    ([first], [second]) => first.localeCompare(second),
  );

  /**
   * Read off the row rather than off `openTarget`: the drawer is handed a bare
   * `LedgerAccount`, and the KPI codes are resolved to labelled clauses once, on
   * the page behind.
   */
  const openTargetKpiRelations =
    rows.find((row) => row.ledgerAccount.code === openTarget?.code)
      ?.kpiRelations ?? [];

  return (
    <Transition.Root as={Fragment} show={target !== null}>
      <Dialog
        as="div"
        className="relative z-40"
        initialFocus={searchRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto flex w-screen max-w-md flex-col bg-white dark:bg-slate-900">
                  {/* The crimson band names the drawer, as every other drawer in
                      the system does. Which account is being filled belongs to
                      the body below, where the Required badge can still be red. */}
                  <div className="flex items-center justify-between bg-primary px-4 py-4">
                    <Dialog.Title className="text-lg font-medium text-white">
                      Map QuickBooks accounts
                    </Dialog.Title>
                    <button
                      className="text-white"
                      onClick={onClose}
                      type="button"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  {openTarget && (
                    <>
                      {/* The subject of the drawer, not a section of it — so it
                          carries the weight and takes no rule of its own; the
                          grey section bar below is what separates it. */}
                      <div className="px-5 pb-4 pt-4">
                        <div className="text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Mapping into
                          <span className="text-gray-400 dark:text-gray-500">
                            {" · "}
                            {openTarget.ledgerAccountTypeLabel}
                          </span>
                        </div>
                        {/* The title is the switcher. Stated separately they
                            printed the same account name twice a line apart,
                            and a chevron beside a heading says "this changes"
                            more directly than a labelled control below it. */}
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                          <Select
                            onValueChange={onSwitchTarget}
                            value={openTarget.code}
                          >
                            <SelectTrigger className="h-auto w-auto max-w-full justify-start gap-2 border-0 bg-transparent px-0 py-0 text-lg font-semibold text-gray-900 shadow-none transition-colors hover:text-primary focus:ring-0 focus:ring-offset-0 dark:text-white dark:hover:text-primary">
                              {/* The list carries a count per account; the
                                  heading wants the name on its own. */}
                              <SelectValue>{openTarget.label}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {switcherGroups.map(([typeLabel, group]) => (
                                <SelectGroup key={typeLabel}>
                                  <SelectLabel>{typeLabel}</SelectLabel>
                                  {group.map((row) => (
                                    <SelectItem
                                      key={row.ledgerAccount.code}
                                      value={row.ledgerAccount.code}
                                    >
                                      {row.ledgerAccount.label} (
                                      {row.accounts.length})
                                      {row.state === "gap" && (
                                        <span className="text-gray-500 dark:text-gray-400">
                                          {" · empty"}
                                        </span>
                                      )}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              ))}
                            </SelectContent>
                          </Select>
                          {openTarget.isRequired && (
                            <Badge
                              className="px-2 py-0 text-[10px]"
                              variant="destructive"
                            >
                              Required
                            </Badge>
                          )}
                        </div>
                        {/* The drawer is where the choice is actually made, so
                            the row's description and the cards that read it
                            follow it in rather than staying behind on the
                            page. */}
                        {LEDGER_ACCOUNT_DESCRIPTIONS[openTarget.code] !==
                          undefined && (
                          <p className="pt-1 text-xs text-gray-500 dark:text-gray-400">
                            {LEDGER_ACCOUNT_DESCRIPTIONS[openTarget.code]}
                          </p>
                        )}
                        <KpiRelationsLine relations={openTargetKpiRelations} />
                      </div>

                      <SectionHeading
                        count={accountsInTarget.length}
                        title="In this account now"
                      />
                      {/* Capped and scrollable: one row each reads far better
                          than a wrapping run of chips, but a well-filled account
                          would otherwise push the pick list off the panel. */}
                      <div className="max-h-48 shrink-0 space-y-2 overflow-y-auto px-5 py-3">
                        {accountsInTarget.length === 0 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Nothing is mapped here yet.
                          </p>
                        )}
                        {accountsInTarget.map((account) => {
                          const isPending =
                            account.mapping?.isConfirmed === false;

                          return (
                            /* The same card the page behind gives a mapped
                               account — one per row, amber while unreviewed —
                               so the drawer and the list read as one thing. */
                            <div
                              className={`flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2 text-xs dark:border-slate-700 ${
                                isPending
                                  ? "bg-amber-50/60 dark:bg-amber-950/20"
                                  : ""
                              }`}
                              key={account.qboAccountId}
                            >
                              <span className="flex min-w-0 items-center gap-2">
                                <span className="min-w-0 truncate">
                                  <span className="text-gray-900 dark:text-white">
                                    {account.name}
                                  </span>
                                  {account.accountNumber && (
                                    <span className="text-gray-400 dark:text-gray-500">
                                      {" \u00b7 "}
                                      {account.accountNumber}
                                    </span>
                                  )}
                                </span>
                                {/* Never filtered: a retired account the member
                                    mapped still feeds this total, and hiding it
                                    would leave the figure unexplainable. */}
                                {!account.isActive && <InactiveBadge />}
                              </span>

                              <span className="flex shrink-0 items-center gap-1">
                                {savingAccountIds.has(account.qboAccountId) ? (
                                  <span
                                    className="inline-flex h-5 w-5 items-center justify-center text-gray-500 dark:text-gray-400"
                                    title="Saving\u2026"
                                  >
                                    <Loader2
                                      className="h-3.5 w-3.5 animate-spin"
                                      strokeWidth={1.75}
                                    />
                                  </span>
                                ) : (
                                  <>
                                    {isPending && (
                                      <button
                                        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-200 text-amber-900 transition-colors hover:bg-emerald-500 hover:text-white dark:bg-amber-900 dark:text-amber-100 dark:hover:bg-emerald-600 dark:hover:text-white"
                                        onClick={() =>
                                          onConfirm(account.qboAccountId)
                                        }
                                        title="Accept this mapping"
                                        type="button"
                                      >
                                        <Check className="h-3 w-3" />
                                      </button>
                                    )}
                                    <button
                                      className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 text-gray-400 transition-colors hover:border-rose-300 hover:bg-rose-100 hover:text-rose-700 dark:border-slate-600 dark:text-gray-500 dark:hover:border-rose-900 dark:hover:bg-rose-950 dark:hover:text-rose-200"
                                      onClick={() =>
                                        onUnassign(account.qboAccountId)
                                      }
                                      title="Remove \u2014 it goes back to your unplaced list"
                                      type="button"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      <SectionHeading
                        action={
                          hiddenInactiveCount > 0 || showInactive ? (
                            <button
                              className="text-[11px] font-normal normal-case tracking-normal text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                              onClick={() => onToggleInactive(!showInactive)}
                              type="button"
                            >
                              {showInactive
                                ? "Hide inactive"
                                : `Show inactive (${hiddenInactiveCount})`}
                            </button>
                          ) : undefined
                        }
                        count={candidates.length}
                        title="Add accounts"
                      />
                      <div className="relative shrink-0 px-5 py-3">
                        <Search className="absolute left-8 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                        <input
                          className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-500 focus:border-gray-400 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder:text-gray-400"
                          onChange={(event) => setQuery(event.target.value)}
                          placeholder="Search all your QuickBooks accounts…"
                          ref={(element) => {
                            searchRef.current = element;
                          }}
                          value={query}
                        />
                      </div>

                      <div className="flex-1 overflow-y-auto">
                        {candidates.map((candidate, index) => {
                          const { account } = candidate;
                          const previous = candidates[index - 1];
                          // Retired accounts sit below every live one, under a
                          // heading of their own. Without this they would reopen
                          // a tier heading that has already been and gone.
                          const isFirstInactive =
                            !account.isActive &&
                            (index === 0 || previous.account.isActive);
                          const isNewTier =
                            account.isActive &&
                            (index === 0 || previous.tier !== candidate.tier);

                          return (
                            <div key={account.qboAccountId}>
                              {(isNewTier || isFirstInactive) && (
                                <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-y border-gray-100 bg-gray-50 px-5 py-1.5 dark:border-slate-700/50 dark:bg-slate-800/90">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {isFirstInactive
                                      ? "Inactive in QuickBooks"
                                      : PICKER_TIER_LABELS[candidate.tier]}
                                  </span>
                                  {isNewTier && candidate.tier === "likely" && (
                                    <button
                                      className="text-xs text-primary transition-colors hover:underline"
                                      onClick={() =>
                                        onSelectAllInTier("likely")
                                      }
                                      type="button"
                                    >
                                      Select all
                                    </button>
                                  )}
                                </div>
                              )}

                              <label
                                className={`flex cursor-pointer items-start gap-2.5 border-b border-gray-100 px-5 py-2.5 transition-colors dark:border-slate-700/50 ${
                                  selectedIds.has(account.qboAccountId)
                                    ? "bg-gray-100 dark:bg-slate-700/60"
                                    : "hover:bg-gray-50 dark:hover:bg-slate-700/30"
                                }`}
                              >
                                <Checkbox
                                  checked={selectedIds.has(
                                    account.qboAccountId,
                                  )}
                                  className="mt-0.5 cursor-pointer"
                                  onCheckedChange={() =>
                                    onToggleSelected(account.qboAccountId)
                                  }
                                  size="sm"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                                    <span className="min-w-0 truncate">
                                      {account.name}
                                      {account.accountNumber && (
                                        <span className="text-gray-500 dark:text-gray-400">
                                          {" · "}
                                          {account.accountNumber}
                                        </span>
                                      )}
                                    </span>
                                    {!account.isActive && <InactiveBadge />}
                                    {/* Which of the retired accounts actually
                                        matter. Without it the member is handed a
                                        list and no way to tell the two that cost
                                        them a figure from the twenty that cost
                                        them nothing. */}
                                    {!account.isActive &&
                                      account.hasRecentBalance &&
                                      account.mapping === null && (
                                        <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-slate-700 dark:text-gray-300">
                                          Held a balance
                                        </span>
                                      )}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {account.accountType ?? "—"} →{" "}
                                    {account.accountSubType ?? "—"}
                                  </div>
                                  {candidate.tier === "elsewhere" &&
                                    account.mapping && (
                                      <div className="mt-0.5 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300">
                                        <Info className="mt-0.5 h-3 w-3 shrink-0" />
                                        <span>
                                          In “
                                          {account.mapping.ledgerAccountLabel}”
                                          — adding it here moves it.
                                        </span>
                                      </div>
                                    )}
                                </div>
                              </label>
                            </div>
                          );
                        })}

                        {candidates.length === 0 && (
                          <div className="px-5 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                            {query === ""
                              ? "Every one of your accounts is already in this one."
                              : "No accounts match that search."}
                          </div>
                        )}
                      </div>

                      <div className="border-t border-gray-200 px-5 py-3 dark:border-slate-700">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {selectedCount === 0 ? (
                              "Tick the accounts that belong here."
                            ) : (
                              <>
                                <span className="text-gray-900 dark:text-white">
                                  {selectedCount} selected
                                </span>
                                {movingCount > 0 && (
                                  <span className="text-amber-700 dark:text-amber-300">
                                    {" · "}
                                    {movingCount} will move
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          <Button
                            className="rounded-md"
                            disabled={selectedCount === 0}
                            onClick={onCommit}
                            size="sm"
                          >
                            <Check />
                            Add{selectedCount > 0 ? ` ${selectedCount}` : ""}
                          </Button>
                        </div>

                        <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-gray-100 pt-2.5 dark:border-slate-700/50">
                          <button
                            className="text-xs text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                            onClick={onClose}
                            type="button"
                          >
                            Close and review the list
                          </button>
                          {nextNeedy && (
                            <button
                              className="inline-flex items-center gap-1 text-xs text-primary transition-colors hover:underline"
                              onClick={() => onSwitchTarget(nextNeedy.code)}
                              type="button"
                            >
                              Next: {nextNeedy.label}
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
